/**
 * Display vocabulary and derived figures for the Liquidity Network list pages
 * (Offerings, Positions).
 *
 * Label maps are keyed by the GENERATED unions of
 * loan-syndication-protocol.openapi.yaml, so a status the spec adds fails tsc
 * here until it has a label. Absent optional fields print words that say what
 * is absent ("Not evaluated", "Not reported"); none of these helpers turns a
 * missing value into a measured 0 (.claude/rules/no-fabricated-values.md).
 */

import type { components as SyndicationComponents } from '@aaimplatform/institutional-types/generated/loan-syndication-protocol';
import { enumLabel } from '../../utils/status-labels';
import { ASSET_CLASS_LABELS } from '../../generated/terminology-labels';
import { formatRatioAsPercent } from '../../utils/display-format';

type ParticipationOffering = SyndicationComponents['schemas']['ParticipationOffering'];
type ParticipationPosition = SyndicationComponents['schemas']['ParticipationPosition'];
type CollateralSummary = SyndicationComponents['schemas']['CollateralSummary'];

/**
 * What the operator Offerings and Positions pages read. GET /offerings and
 * GET /positions scope to the caller's resolved tenant, which is the Aaim
 * platform tenant for an operator (resolveEffectiveTenantId), so both lists
 * are that tenant's own book. Offerings other institutions list on the
 * network appear in GET /marketplace, which these pages do not read. Until a
 * network scope is declared for the operator, each page states its scope so
 * an empty list does not read as an empty network.
 */
export const OFFERINGS_SCOPE_NOTE =
  'Offerings originated in the Aaim platform tenant. Offerings that other institutions list on the Liquidity Network do not appear on this page.';

export const POSITIONS_SCOPE_NOTE =
  'Positions on offerings originated in the Aaim platform tenant. Positions on offerings that other institutions originated do not appear on this page.';

/** An offering or position with no collateral evaluation attached. */
export const NOT_EVALUATED_LABEL = 'Not evaluated';

/** An optional field the service returned without a value. */
export const NOT_REPORTED_LABEL = 'Not reported';

export const OFFERING_STATUS_LABEL: Record<ParticipationOffering['status'], string> = {
  OPEN: 'Open',
  FULLY_COMMITTED: 'Fully Committed',
  ACTIVE: 'Active',
  MATURED: 'Matured',
  DEFAULTED: 'Defaulted',
  // term-register-skip: CANCELLED is the spec enum key; the label is US spelling
  CANCELLED: 'Canceled',
};

export const POSITION_STATUS_LABEL: Record<ParticipationPosition['status'], string> = {
  ACTIVE: 'Active',
  MATURED: 'Matured',
  DEFAULTED: 'Defaulted',
  EXITED: 'Exited',
};

export const POSITION_ROLE_LABEL: Record<ParticipationPosition['role'], string> = {
  LEAD: 'Lead',
  PARTICIPANT: 'Participant',
};

/**
 * The LTV of a collateral evaluation. collateralSummary is optional on both
 * ParticipationOffering and ParticipationPosition; an offering with no
 * evaluation read "0.0%" before, a measured-looking figure for an LTV nobody
 * computed.
 */
export function collateralLtvLabel(summary: CollateralSummary | undefined): string {
  return summary ? formatRatioAsPercent(summary.ltvRatio) : NOT_EVALUATED_LABEL;
}

/**
 * The asset class of a collateral evaluation, labeled from the terminology
 * register (generated/terminology-labels.ts). primaryAssetClass is the
 * Reference Model taxonomy code; a code outside the map prints "Unrecognized".
 */
export function collateralAssetClassLabel(summary: CollateralSummary | undefined): string {
  if (!summary) return NOT_EVALUATED_LABEL;
  if (!summary.primaryAssetClass) return NOT_REPORTED_LABEL;
  return enumLabel(ASSET_CLASS_LABELS, summary.primaryAssetClass);
}

/** annualizedYield is optional on a position; absent prints "Not reported". */
export function positionYieldLabel(yieldRatio: number | undefined): string {
  return typeof yieldRatio === 'number' && Number.isFinite(yieldRatio)
    ? formatRatioAsPercent(yieldRatio)
    : NOT_REPORTED_LABEL;
}

/**
 * The spec's PositionSummary.averageYield: "Weighted average annualized
 * yield across active positions". Weighted by committedAmount, over ACTIVE
 * positions that report a yield. `value` is null when no active position
 * reports one, so the page prints an absence instead of a measured 0%.
 */
export function weightedAverageYield(positions: readonly ParticipationPosition[]): {
  value: number | null;
  activeCount: number;
  reportingCount: number;
} {
  let activeCount = 0;
  let reportingCount = 0;
  let weight = 0;
  let weighted = 0;
  for (const position of positions) {
    if (position.status !== 'ACTIVE') continue;
    activeCount += 1;
    const y = position.annualizedYield;
    if (typeof y !== 'number' || !Number.isFinite(y)) continue;
    if (!Number.isFinite(position.committedAmount) || position.committedAmount <= 0) continue;
    reportingCount += 1;
    weight += position.committedAmount;
    weighted += y * position.committedAmount;
  }
  return {
    value: weight > 0 ? weighted / weight : null,
    activeCount,
    reportingCount,
  };
}
